/** docs/UX.md 16 · `/m/quality` 质量看板（含 B5 周度 IAA） */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, EmptyState, Icon, Select, Table } from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { qualityApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import {
  ErrorNotice,
  GateLight,
  LoadingBlock,
  PageHeader,
  QueryBoundary,
  Section,
  StatusTag,
  formatNumber,
  formatPercent,
} from "./common";
import type {
  QualityWeeklyResponse,
  WeeklyAnnotatorStat,
  WeeklyPairStat,
} from "../annotator/types";
import type {
  AnnotatorStat,
  QualityAnnotatorsResponse,
  QualityBatchMetrics,
  QualitySummaryResponse,
} from "./types";

interface BatchRow extends QualityBatchMetrics {
  batch_id: string;
}

/** Icon + wording + number — a pass/fail badge never rides on colour alone. */
function PassTag({ pass, children }: { pass: boolean | null | undefined; children: ReactNode }) {
  if (pass === null || pass === undefined) {
    return <StatusTag icon="ui-help">{children}</StatusTag>;
  }
  return (
    <StatusTag icon={pass ? "ui-check-circle" : "ui-warning"} tone={pass ? "success" : "danger"}>
      {children}
    </StatusTag>
  );
}

export function QualityPage() {
  const summary = useQuery({
    queryKey: qk.qualitySummary,
    queryFn: ({ signal }) => qualityApi.summary<QualitySummaryResponse>(signal),
  });
  const annotators = useQuery({
    queryKey: qk.qualityAnnotators,
    queryFn: ({ signal }) => qualityApi.annotators<QualityAnnotatorsResponse>(signal),
  });
  // docs/ROADMAP.md B5 —— 周度 IAA (ISO 周 · Asia/Shanghai)
  const weekly = useQuery({
    queryKey: qk.qualityWeekly(8),
    queryFn: ({ signal }) => qualityApi.weekly({ weeks: 8 }, signal),
    retry: false,
  });

  const gates = summary.data?.gates;
  const overall = summary.data?.overall;

  const batchRows = useMemo<BatchRow[]>(
    () =>
      Object.entries(summary.data?.by_batch ?? {})
        .map(([batch_id, m]) => ({ batch_id, ...m }))
        .sort((a, b) => a.batch_id.localeCompare(b.batch_id)),
    [summary.data],
  );

  const trend = summary.data?.arbitration_trend ?? [];
  const arbGate = gates?.arbitration_ratio ?? 0.12;

  const annotatorColumns: Array<Column<AnnotatorStat>> = [
    { key: "name", header: "专家", render: (a) => a.display_name },
    { key: "today", header: "今日", align: "end", render: (a) => a.today },
    { key: "total", header: "累计", align: "end", render: (a) => a.total },
    {
      key: "merged",
      header: "已汇总",
      align: "end",
      hideOnMobile: true,
      render: (a) => a.merged_with_peers,
    },
    {
      key: "kappa",
      header: "κ",
      render: (a) => <PassTag pass={a.kappa_pass}>κ {formatNumber(a.kappa)}</PassTag>,
    },
    { key: "agreement", header: "一致率", render: (a) => formatPercent(a.agreement_rate) },
    {
      key: "golden",
      header: "黄金题命中",
      render: (a) => <PassTag pass={a.golden_pass}>{formatPercent(a.golden_hit_rate)}</PassTag>,
    },
  ];

  const weeklyColumns: Array<Column<WeeklyAnnotatorStat>> = [
    { key: "name", header: "专家", render: (a) => a.display_name },
    { key: "recordings", header: "配对录音", align: "end", render: (a) => a.recordings },
    {
      key: "calibration",
      header: "其中校准批",
      align: "end",
      hideOnMobile: true,
      render: (a) => a.calibration_recordings,
    },
    {
      key: "kappa",
      header: "κ",
      render: (a) => (
        <PassTag pass={a.pass}>κ {a.kappa === null ? "—" : formatNumber(a.kappa)}</PassTag>
      ),
    },
    {
      key: "agreement",
      header: "一致率",
      render: (a) => formatPercent(a.agreement_rate),
    },
    {
      key: "hint",
      header: "建议",
      render: (a) => (a.pass === false ? "本周未达标，建议先做校准批" : "—"),
    },
  ];

  const weeklyPairColumns: Array<Column<WeeklyPairStat>> = [
    {
      key: "pair",
      header: "配对",
      render: (p) => `${p.a_name} ↔ ${p.b_name}`,
    },
    { key: "recordings", header: "共同录音", align: "end", render: (p) => p.recordings },
    {
      key: "kappa",
      header: "κ",
      render: (p) => (
        <PassTag pass={p.pass}>κ {p.kappa === null ? "—" : formatNumber(p.kappa)}</PassTag>
      ),
    },
    {
      key: "agreement",
      header: "一致率",
      render: (p) => formatPercent(p.agreement_rate),
    },
  ];

  const batchColumns: Array<Column<BatchRow>> = [
    {
      key: "batch",
      header: "批次",
      render: (b) => <span className="xz-adm__mono">{b.batch_id}</span>,
    },
    { key: "recordings", header: "已汇总录音", align: "end", render: (b) => b.recordings },
    {
      key: "kappa",
      header: "κ",
      render: (b) => <PassTag pass={b.kappa_pass}>{formatNumber(b.kappa)}</PassTag>,
    },
    {
      key: "agreement",
      header: "一致率",
      render: (b) => <PassTag pass={b.agreement_pass}>{formatPercent(b.agreement_rate)}</PassTag>,
    },
    {
      key: "arbitration",
      header: "仲裁占比",
      render: (b) => (
        <PassTag pass={b.arbitration_pass}>{formatPercent(b.arbitration_ratio)}</PassTag>
      ),
    },
  ];

  return (
    <div className="xz-adm">
      <PageHeader
        title="质量看板"
        description="§10.2 四项门槛与各专家指标。这里只有计数与统计，看不到任何判断内容。"
        actions={
          <Button
            variant="secondary"
            leadingIcon={<Icon id="ui-refresh" size={16} />}
            onClick={() => {
              void summary.refetch();
              void annotators.refetch();
              void weekly.refetch();
            }}
          >
            刷新
          </Button>
        }
      />

      {summary.isPending ? (
        <LoadingBlock rows={3} label="正在加载质量指标" />
      ) : summary.isError ? (
        <ErrorNotice
          error={summary.error}
          onRetry={() => void summary.refetch()}
          title="质量指标没能加载"
        />
      ) : (
        <>
          <Section title="总体门槛" subtitle={`已汇总 ${overall?.merged_recordings ?? 0} 条录音`}>
            <div className="xz-gates">
              <GateLight
                icon="ui-target"
                label="κ 一致性"
                value={overall?.kappa ?? null}
                pass={overall?.kappa_pass ?? null}
                gate={gates?.kappa ?? 0.8}
                compare=">="
                percent={false}
              />
              <GateLight
                icon="ui-percent"
                label="一致率"
                value={overall?.agreement_rate ?? null}
                pass={overall?.agreement_pass ?? null}
                gate={gates?.agreement_rate ?? 0.85}
                compare=">="
              />
              <GateLight
                icon="ui-swap"
                label="仲裁占比"
                value={overall?.arbitration_ratio ?? null}
                pass={overall?.arbitration_pass ?? null}
                gate={arbGate}
                compare="<="
              />
              <GateLight
                icon="ui-star"
                label="黄金题命中"
                value={overall?.golden_hit_rate ?? null}
                pass={overall?.golden_pass ?? null}
                gate={gates?.golden_hit_rate ?? 0.9}
                compare=">="
              />
            </div>
            <p className="xz-adm__rowactions" style={{ marginTop: "var(--gz-sp-4)" }}>
              <StatusTag
                icon="ui-swap"
                tone={(summary.data.pending_arbitration ?? 0) > 0 ? "warning" : "neutral"}
              >
                待仲裁 {summary.data.pending_arbitration ?? 0} 条
              </StatusTag>
            </p>
          </Section>

          {trend.length > 0 && (
            <Section title="仲裁占比趋势" subtitle={`门槛 ≤ ${formatPercent(arbGate, 0)}`}>
              <ul className="xz-trend" aria-label="仲裁占比趋势">
                {trend.map((p) => (
                  <li key={p.label} className="xz-trend__item" data-over={p.value > arbGate}>
                    <span className="gz-caption">{formatPercent(p.value, 0)}</span>
                    <span
                      className="xz-trend__bar"
                      style={{ blockSize: `${Math.max(2, Math.min(100, p.value * 400))}%` }}
                      aria-hidden="true"
                    />
                    <span className="xz-trend__label">{p.label}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title="按批次" subtitle="每个批次的一致性与仲裁占比">
            <Table
              caption="按批次的质量指标"
              columns={batchColumns}
              rows={batchRows}
              rowKey={(b) => b.batch_id}
              empty={
                <EmptyState
                  title="还没有汇总数据"
                  description="等两位专家都提交后，指标才会出现。"
                />
              }
            />
          </Section>
        </>
      )}

      <WeeklyIaaSection
        data={weekly.data}
        isPending={weekly.isPending}
        isError={weekly.isError}
        error={weekly.error}
        onRetry={() => void weekly.refetch()}
        annotatorColumns={weeklyColumns}
        pairColumns={weeklyPairColumns}
      />

      <Section title="按专家" subtitle="计数、今日量、κ 与黄金题命中率">
        <QueryBoundary
          isPending={annotators.isPending}
          isError={annotators.isError}
          error={annotators.error}
          onRetry={() => void annotators.refetch()}
          skeletonRows={2}
        >
          <Table
            caption="各专家指标"
            columns={annotatorColumns}
            rows={annotators.data?.annotators ?? []}
            rowKey={(a) => a.annotator_id}
            empty={
              <EmptyState
                title="还没有标注专家"
                description="到「账号」里给专家开通 annotator 角色。"
              />
            }
          />
        </QueryBoundary>
      </Section>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// B5 周度 IAA —— ISO 周（周一至周日，Asia/Shanghai）
// --------------------------------------------------------------------------- //

interface WeeklyIaaSectionProps {
  data: QualityWeeklyResponse | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  annotatorColumns: Array<Column<WeeklyAnnotatorStat>>;
  pairColumns: Array<Column<WeeklyPairStat>>;
}

/**
 * 一周一屏：选周 → 看每位专家的配对 κ / 一致率 → 未达标的那一行直接写着
 * 「本周未达标，建议先做校准批」。默认只是建议；`XZ_IAA_ENFORCE=on` 时会额外
 * 说明队列已经被扣住。
 */
function WeeklyIaaSection({
  data,
  isPending,
  isError,
  error,
  onRetry,
  annotatorColumns,
  pairColumns,
}: WeeklyIaaSectionProps) {
  const weeks = data?.weeks ?? [];
  const [picked, setPicked] = useState<string>("");
  const current = weeks.find((w) => w.week === picked) ?? weeks[0] ?? null;
  const gate = data?.gate ?? 0.8;
  const failing = (current?.annotators ?? []).filter((a) => a.pass === false);

  return (
    <Section
      title="周度一致性（IAA）"
      subtitle={`按 ISO 周统计每位专家的配对 κ，目标 κ ≥ ${formatNumber(gate)}（北京时间）`}
      actions={
        weeks.length > 1 ? (
          <Select
            aria-label="选择周次"
            value={current?.week ?? ""}
            onChange={(e) => setPicked(e.target.value)}
          >
            {weeks.map((w) => (
              <option key={w.week} value={w.week}>
                {w.week}（{w.start} – {w.end}）
              </option>
            ))}
          </Select>
        ) : undefined
      }
    >
      <QueryBoundary
        isPending={isPending}
        isError={isError}
        error={error}
        onRetry={onRetry}
        skeletonRows={2}
      >
        {!current ? (
          <EmptyState
            title="还没有可比的周度数据"
            description="同一条录音要有两位专家都提交过，才能算出 κ。校准批是最快的办法。"
          />
        ) : (
          <>
            {failing.length > 0 && (
              <p className="gz-notice gz-notice--error" role="status">
                <Icon id="ui-warning" size={20} />
                <span>
                  {failing.map((a) => a.display_name).join("、")}
                  本周未达标，建议先做校准批。
                  {data?.enforce
                    ? "（XZ_IAA_ENFORCE=on：校准批达标前不再派新的普通条目。）"
                    : "（仅提示，队列不受影响。）"}
                </span>
              </p>
            )}
            <Table
              caption={`${current.week} 各专家的配对一致性`}
              columns={annotatorColumns}
              rows={current.annotators}
              rowKey={(a) => a.annotator_id}
              empty={<EmptyState title="这一周还没有配对数据" />}
            />
            {current.pairs.length > 0 && (
              <Table
                caption={`${current.week} 按配对`}
                columns={pairColumns}
                rows={current.pairs}
                rowKey={(p) => `${p.a}|${p.b}`}
              />
            )}
          </>
        )}
      </QueryBoundary>
    </Section>
  );
}
